// src/lib/constants/appFactory.ts

// ── Build statuses (factory app_build2 machine) ──
export const FACTORY_BUILD_STATUSES = [
  'queued',
  'building',
  'built',
  'preflight_passed',
  'awaiting_customer',
  'failed',
  'superseded',
] as const;
export type FactoryBuildStatus = typeof FACTORY_BUILD_STATUSES[number];

/**
 * A build the farm still owns: queued, leased, or built but not yet checked.
 * `preflight_passed` / `awaiting_customer` are RESTING rows (a new request for
 * the same release supersedes them — a brand fix is real work again);
 * `failed` / `superseded` are terminal.
 */
export const FACTORY_ACTIVE_BUILD_STATUSES = ['queued', 'building', 'built'] as const;

/** A build resting at its checks: built, verified, waiting for a submission or the holder. */
export const FACTORY_RESTING_BUILD_STATUSES = ['preflight_passed', 'awaiting_customer'] as const;

/**
 * A submission a store holds or serves, or the holder still has to hand it —
 * or (3.34.4) one the factory is still preparing: the build behind it is the
 * submission's from the moment the pre-flight opened it, so the Build card does
 * not offer the same release again (the farm would answer the same row — or,
 * once live, only a changed set of inputs is real work: appfactory ADR-0033).
 */
export const FACTORY_STORE_BOUND_SUBMISSION_STATES = [
  'preparing',
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
  'live',
] as const;

// ── Submission states (factory app_submission machine) ──
export const FACTORY_SUBMISSION_STATES = [
  'preparing',
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
  'rejected',
  'live',
  'superseded',
] as const;
export type FactorySubmissionState = typeof FACTORY_SUBMISSION_STATES[number];

// ── Display labels ──
export const FACTORY_BUILD_STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  building: 'Building',
  built: 'Built',
  preflight_passed: 'Checks Passed',
  awaiting_customer: 'Ready for store',
  failed: 'Failed',
  superseded: 'Superseded',
};

export const FACTORY_SUBMISSION_STATE_LABELS: Record<string, string> = {
  preparing: 'Preparing',
  awaiting_customer: 'Ready to submit',
  submitting: 'Submitting',
  in_review: 'In Review',
  approved: 'Approved',
  rejected: 'Rejected',
  live: 'Live',
  superseded: 'Superseded',
};

// ── Badge colors (Mantine palette names) ──
export const FACTORY_BUILD_STATUS_COLORS: Record<string, string> = {
  queued: 'gray',
  building: 'blue',
  built: 'cyan',
  preflight_passed: 'teal',
  awaiting_customer: 'orange',
  failed: 'red',
  superseded: 'gray',
};

export const FACTORY_SUBMISSION_STATE_COLORS: Record<string, string> = {
  preparing: 'gray',
  awaiting_customer: 'orange',
  submitting: 'blue',
  in_review: 'indigo',
  approved: 'teal',
  rejected: 'red',
  live: 'green',
  superseded: 'gray',
};

/**
 * One line of state-specific guidance under a submission's badge — what the
 * state MEANS for the person reading the card (B1: the generic "waiting on
 * your action" badge told nobody what to do).
 */
export const FACTORY_SUBMISSION_STATE_GUIDANCE: Record<string, string> = {
  preparing: 'The build is being checked and packaged. Nothing to do yet.',
  awaiting_customer: 'The package and guide are ready for the store portal.',
  submitting: 'The package is being entered in the store portal.',
  in_review: 'The store is reviewing the channel. Reviews usually take a few business days.',
  approved: 'The store approved the channel. It goes live on the scheduled publish date.',
  rejected: 'The store rejected this submission. A new build starts a fresh submission.',
  live: 'This version is live in the store.',
  superseded: 'A newer submission replaced this one.',
};

/**
 * Store-specific wording over the guidance above (which speaks Roku's "channel"
 * and "store portal"); a state a store does not list falls back to it.
 */
export const FACTORY_STORE_STATE_GUIDANCE: Partial<Record<string, Record<string, string>>> = {
  amazon: {
    preparing: 'The update is being built and checked. Nothing to do yet.',
    awaiting_customer: 'The update is built, checked and ready for the Amazon Appstore.',
    submitting: 'The update is being submitted to the Amazon Appstore.',
    in_review: 'Amazon is reviewing the update. Reviews usually take a few days.',
    approved: 'Amazon approved the update. It reaches devices shortly after approval.',
    rejected: 'Amazon rejected this update. A new build starts a fresh submission.',
  },
  google: {
    preparing: 'The update is being built and checked. Nothing to do yet.',
    awaiting_customer: 'The update is built, checked and ready for Google Play.',
    submitting: 'The update is being published to Google Play.',
    in_review: 'Google Play is reviewing the update. Reviews usually take a few days.',
    approved: 'Google Play approved the update. It reaches devices shortly after.',
    rejected: 'Google Play rejected this update. A new build starts a fresh submission.',
  },
};

// ── Store accounts (4.2 delegated store accounts) ──
export const FACTORY_STORE_ACCOUNT_STATES = [
  'pending',
  'invited',
  'active',
  'action_needed',
  'revoked',
] as const;
export type FactoryStoreAccountState = typeof FACTORY_STORE_ACCOUNT_STATES[number];

export const FACTORY_STORE_ACCOUNT_STATE_LABELS: Record<string, string> = {
  pending: 'Not set up',
  invited: 'Invite sent',
  active: 'Active',
  action_needed: 'Action needed',
  revoked: 'Revoked',
};

export const FACTORY_STORE_ACCOUNT_STATE_COLORS: Record<string, string> = {
  pending: 'gray',
  invited: 'blue',
  active: 'green',
  action_needed: 'orange',
  revoked: 'red',
};

export const FACTORY_STORE_ACCOUNT_HOLDER_KINDS = ['customer', 'reseller', 'streamhoster'] as const;
export type FactoryStoreAccountHolderKind = typeof FACTORY_STORE_ACCOUNT_HOLDER_KINDS[number];

export const FACTORY_STORE_ACCOUNT_SUBMITTERS = ['operator', 'holder'] as const;
export type FactoryStoreAccountSubmitter = typeof FACTORY_STORE_ACCOUNT_SUBMITTERS[number];

export const FACTORY_STORE_ACCOUNT_CEREMONIES = ['operator', 'reseller', 'customer'] as const;

/** Stores the App Center onboards today (Apple comes with its shell). */
export const FACTORY_STORES = ['roku', 'amazon', 'google'] as const;
export type FactoryStoreKey = typeof FACTORY_STORES[number];

export interface FactoryStoreInfo {
  key: FactoryStoreKey;
  label: string;
  /** The store's developer portal. */
  portalUrl: string;
  /** Where in the portal the user is added (one sentence, verified in the demo walk). */
  inviteLocation: string;
  /** The download button on a submission card: what the store's binary is. */
  packageLabel: string;
  /**
   * The build system itself records "Ready to submit" the moment a build passes
   * its checks (Amazon, Google Play). With a delegated account that state is
   * never a task for the customer: the operator submits the update.
   */
  readyIsAutomatic: boolean;
  /**
   * The store publishes every app from the OPERATOR's own developer account
   * (Google Play): no customer account, no invite, nothing to set up. The
   * readiness chip says who publishes, the wizard's store step has no card for
   * it, and every submission is handled for the customer.
   */
  operatorAccount?: boolean;
}

export const FACTORY_STORE_INFO: Record<FactoryStoreKey, FactoryStoreInfo> = {
  roku: {
    key: 'roku',
    label: 'Roku',
    portalUrl: 'https://developer.roku.com',
    inviteLocation: 'Manage My Account → Users → Add User',
    packageLabel: 'Download package (.pkg)',
    readyIsAutomatic: false,
  },
  amazon: {
    key: 'amazon',
    label: 'Amazon Appstore',
    portalUrl: 'https://developer.amazon.com',
    inviteLocation: 'My Settings → User Permissions → Add User',
    packageLabel: 'Download app file (.apk)',
    readyIsAutomatic: true,
  },
  google: {
    key: 'google',
    label: 'Google Play',
    portalUrl: 'https://play.google.com/console',
    inviteLocation: '',
    packageLabel: 'Download app bundle (.aab)',
    readyIsAutomatic: true,
    operatorAccount: true,
  },
};

/**
 * A store whose FIRST version cannot be submitted for the customer by the build
 * system (Amazon and Google Play: their publishing services only update an app
 * that already exists in the console). Shown on the platform's Status tab until
 * the store's app id is recorded — or, for a store that knows the app by its
 * package name, until a submission has gone past Ready to submit; stores
 * without an entry have no such step.
 */
export interface FactoryStoreFirstSubmission {
  title: string;
  facts: string[];
  /** Who does it, per the account's submitter; `{operator}` = the operator's name. */
  byOperator: string;
  byHolder: string;
  noAccount: string;
  after: string;
}

export const FACTORY_STORE_FIRST_SUBMISSION: Partial<Record<FactoryStoreKey, FactoryStoreFirstSubmission>> = {
  amazon: {
    title: 'The first Amazon submission is done in the Amazon developer console',
    facts: [
      'Amazon only accepts automatic submissions for an app that already exists in its developer console, so the first version is entered there by hand.',
      'That first version sets what the console keeps for every later one: availability and pricing, the content rating, the store listing and images, and the first app file.',
    ],
    byOperator:
      '{operator} enters the first version for you through your Amazon developer account once the first build passes its checks — nothing to do.',
    byHolder:
      'You create the app in the Amazon developer console, paste its Amazon App ID on the Settings tab, and enter the first version with the app file from the submission card below — then record each step here.',
    noAccount:
      'Set up the Amazon developer account first — the Readiness card below opens that step.',
    after:
      'Once the Amazon App ID is recorded and Amazon approves the first version, every later update is submitted without the console.',
  },
  // Published from the operator's own Play developer account: there is no
  // holder path and no account step, so the three "who" sentences are one.
  google: {
    title: 'The first Google Play release is done in the Play Console',
    facts: [
      'Google Play publishes automatic updates only for an app that already exists in the Play Console, so the first version is created there by hand.',
      'That first version sets what the console keeps for every later one: the Android TV form factor, the Data safety and content-rating declarations, the target audience, the privacy policy, the store listing and images, and the first app bundle.',
    ],
    byOperator:
      "{operator} creates the app in the Play Console under its own Google Play developer account and publishes the first version once the first build passes its checks — nothing to do.",
    byHolder:
      "{operator} creates the app in the Play Console under its own Google Play developer account and publishes the first version once the first build passes its checks — nothing to do.",
    noAccount:
      "{operator} creates the app in the Play Console under its own Google Play developer account and publishes the first version once the first build passes its checks — nothing to do.",
    after:
      'Once the first version is published, every later update is published to Google Play without the console.',
  },
};

/**
 * The build request's refusals, in the customer's words (holder-requested
 * builds, 3.34.0). Keys are the factory's 422 tokens: the admission reasons
 * (`problems[].field`) and the top-level codes. `{platform}` = the platform's
 * label, `{supportContact}` = the brand's support contact; an unknown token
 * falls back to the factory's own sentence.
 */
export const FACTORY_BUILD_REFUSALS: Record<string, string> = {
  // admission reasons (problems[].field)
  missing_images: "Add your app's images first — open the Images tab and approve a set.",
  no_web_domain: "Set up your app's address first (Store Setup).",
  no_content_tree: "Add categories or series to your app's content first.",
  empty_content_tree: "Add categories or series to your app's content first.",
  no_signing_key: 'The Roku signing key for this app is not set up yet — contact {supportContact}.',
  no_platform_row: 'Turn the {platform} app on in Settings first.',
  platform_inactive: 'Turn the {platform} app on in Settings first.',
  no_platform_title: 'Enter the App Name in Settings first.',
  no_store_package_id: "The app's package name is missing or invalid — contact {supportContact}.",
  bad_store_package_id: "The app's package name is missing or invalid — contact {supportContact}.",
  release_not_for_platform: 'That app version is not available for {platform}.',
  // top-level codes
  platform_not_buildable: '{platform} apps cannot be built right now — contact {supportContact}.',
  shell_release_not_found: 'That app version is no longer available. Reload the page and try again.',
};

/** The submission edges a HOLDER (the customer or their reseller) may record; the operator records the rest. */
export const FACTORY_HOLDER_SUBMISSION_FROM_STATES = [
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
] as const;

// ── Query keys ──
export const APP_FACTORY_QUERY_KEYS = {
  all: ['appFactory'] as const,
  storeAccounts: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'storeAccounts', cId] as const,
  releases: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'releases', cId] as const,
  tasks: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'tasks', cId] as const,
  builds: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'builds', cId, appCfgId] as const,
  submissions: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'submissions', cId, appCfgId] as const,
  devices: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'devices', cId] as const,
  activeSubmissions: (cId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'activeSubmissions', cId] as const,
  campaigns: (cId: number) => [...APP_FACTORY_QUERY_KEYS.all, 'campaigns', cId] as const,
  campaignTargets: (cId: number, campaignId: string) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'campaignTargets', cId, campaignId] as const,
  assetSets: (cId: number, appCfgId: number, platform: string) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'assetSets', cId, appCfgId, platform] as const,
} as const;

// ── Asset-set states + labels/colors ──
export const ASSET_SET_STATE_LABELS: Record<string, string> = {
  generated: 'Awaiting Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const ASSET_SET_STATE_COLORS: Record<string, string> = {
  generated: 'orange',
  approved: 'green',
  rejected: 'gray',
};

// ── Deliverable slots (mirrors the factory spec table, assets/specs.go) ──
//
// The manual upload arm builds ONE asset set from these slots; the factory
// re-validates mechanically (ADR-0012 gates) and approval publishes. A
// partial set is legal — it overlays onto the current set. One table per
// platform (3.34.0): the factory stages and promotes each platform's set
// under its own folder and judges it against that platform's spec rows.

/** The platforms the factory has a deliverable set for (the asset-set `platform` field). */
export const FACTORY_ASSET_PLATFORMS = ['roku', 'firetv'] as const;
export type FactoryAssetPlatform = typeof FACTORY_ASSET_PLATFORMS[number];

export interface AssetDeliverableSlot {
  /** Exact spec basename — the manual-mode files key. */
  name: string;
  label: string;
  w: number;
  h: number;
  /** Required encoding, enforced server-side ('png' needs alpha for oh_logo). */
  format: 'png' | 'jpg';
  group: string;
  /**
   * How w x h binds: 'exact' (absent) = the file must be exactly that size;
   * 'max' = a bounded mark — at most that size on each side (the factory
   * also requires it trimmed to its visible edges).
   */
  sizeRule?: 'exact' | 'max';
  /**
   * Other exact sizes the slot still accepts (an 'exact' slot only): the TV
   * banner is 640x360 since TV graphics v2 and 320x180 files stay valid.
   */
  altSizes?: { w: number; h: number }[];
  /** One line under the picker: what the file is, in the customer's words. */
  hint?: string;
}

export const ASSET_DELIVERABLE_GROUPS = {
  PACKAGE: 'Channel package',
  EXPERIENCE: 'In-app experience',
  APP: 'App',
  LISTING: 'Store listing',
} as const;

export const FACTORY_DELIVERABLE_SLOTS: Record<FactoryAssetPlatform, AssetDeliverableSlot[]> = {
  roku: [
    { name: 'icon_fhd.png', label: 'Channel Icon (FHD)', w: 540, h: 405, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'icon_hd.png', label: 'Channel Icon (HD)', w: 290, h: 218, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'icon_sd.png', label: 'Channel Icon (SD)', w: 246, h: 140, format: 'png', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'splash_fhd.jpg', label: 'Splash Screen (FHD)', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'splash_hd.jpg', label: 'Splash Screen (HD)', w: 1280, h: 720, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'splash_sd.jpg', label: 'Splash Screen (SD)', w: 720, h: 480, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.PACKAGE },
    { name: 'bg_fhd.jpg', label: 'App Background', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.EXPERIENCE },
    { name: 'oh_logo_fhd.png', label: 'Overhang Logo (transparent)', w: 400, h: 400, format: 'png', group: ASSET_DELIVERABLE_GROUPS.EXPERIENCE },
    { name: 'oh_bg_fhd.jpg', label: 'Overhang Background', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.EXPERIENCE },
    { name: 'store_poster.png', label: 'Store Poster', w: 540, h: 405, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
    { name: 'screenshot_1.png', label: 'Store Screenshot 1', w: 1920, h: 1080, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
    { name: 'screenshot_2.png', label: 'Store Screenshot 2', w: 1920, h: 1080, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING },
  ],
  // The Android-family set (Fire TV and Google TV build from it — the
  // googletv platform's assetPlatform is firetv; TV graphics v2, design
  // jvm notes/ott/tv-graphics). Each hint names the SCREEN that shows the
  // file: Google TV's Your apps row shows the circle icon (the two layers),
  // its Apps page the banner; a Fire TV home row shows the store listing's
  // 1280x720 tile (never the app's own banner); the stores' listing art is
  // uploaded by whoever publishes the store account, from Current Images.
  firetv: [
    { name: 'icon.png', label: 'App Icon', w: 512, h: 512, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, hint: 'The store icon (Amazon and Google Play) and the app icon in the TV settings.' },
    { name: 'icon-foreground.png', label: 'Google TV Icon (symbol layer)', w: 432, h: 432, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, hint: 'The circle on the Google TV home screen: your symbol on a transparent 432 x 432 PNG, centred.' },
    { name: 'icon-background.png', label: 'Google TV Icon (background layer)', w: 432, h: 432, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, hint: 'The colour behind the symbol in the Google TV circle: an opaque 432 x 432 PNG.' },
    { name: 'tv-banner.png', label: 'TV Banner', w: 640, h: 360, format: 'png', group: ASSET_DELIVERABLE_GROUPS.APP, altSizes: [{ w: 320, h: 180 }], hint: 'The wide tile on the Google TV Apps page (640 x 360; 320 x 180 still accepted).' },
    {
      name: 'splash-logo.png',
      label: 'Splash Logo',
      w: 1152,
      h: 1152,
      format: 'png',
      group: ASSET_DELIVERABLE_GROUPS.APP,
      sizeRule: 'max',
      hint: 'A transparent PNG of your logo mark, trimmed to its edges, up to 1152 x 1152.',
    },
    { name: 'listing-icon-small.png', label: 'Store Icon (small)', w: 114, h: 114, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The small icon of the Amazon listing.' },
    { name: 'listing-icon.png', label: 'Store Icon', w: 1280, h: 720, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The Fire TV home-row tile (from the Amazon listing), and the TV banner of the Google Play listing.' },
    { name: 'listing-background.jpg', label: 'Store Background', w: 1920, h: 1080, format: 'jpg', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The background of the Fire TV app page in the Amazon listing.' },
    { name: 'listing-feature-graphic.png', label: 'Feature Graphic', w: 1024, h: 500, format: 'png', group: ASSET_DELIVERABLE_GROUPS.LISTING, hint: 'The wide image at the top of the Google Play listing.' },
  ],
};

/** The Images surface's wording per platform, so the components never branch on the platform. */
export interface FactoryAssetPlatformCopy {
  label: string;
  appNoun: string;
  /** Under "Current Images". */
  currentIntro: string;
  /** Under "Brand Kit". */
  generateIntro: string;
  /** The Approve & Publish confirmation. */
  approveBody: string;
  /** The notice after approval. */
  publishedMsg: string;
}

export const FACTORY_ASSET_PLATFORM_COPY: Record<FactoryAssetPlatform, FactoryAssetPlatformCopy> = {
  roku: {
    label: 'Roku',
    appNoun: 'Roku app',
    currentIntro:
      'The images your Roku app and store builds use today. Publish a Brand Kit or an uploaded set below to replace them.',
    generateIntro:
      'Generate the full Roku image set from your logo and colors. You review a preview before anything goes live — or upload each image yourself below.',
    approveBody:
      "Approving replaces your Roku app's images with this set. This takes effect in the app and in future store builds.",
    publishedMsg: 'The approved set is now your Roku app artwork.',
  },
  // One set for the Android family: the Fire TV tab and the Google TV tab show
  // the same images, so the copy names both.
  firetv: {
    label: 'Fire TV and Google TV',
    appNoun: 'Fire TV and Google TV apps',
    currentIntro:
      'The images your Fire TV and Google TV apps and their store listings use today. Publish a Brand Kit or an uploaded set below to replace them.',
    generateIntro:
      'Generate the full image set for your Fire TV and Google TV apps — the Google TV circle icon, the Fire TV home tile, the banner, the splash logo and the store listing art — from your symbol, your wordmark and a ground colour. You see each one on a TV screen before anything goes live — or upload each image yourself below.',
    approveBody:
      "Approving replaces your Fire TV and Google TV apps' images with this set. It takes effect in your next build of either.",
    publishedMsg: 'The approved set is now your Fire TV and Google TV app artwork.',
  },
};

/** Per-file cap for manual deliverable uploads. */
export const ASSET_DELIVERABLE_MAX_BYTES = 10 * 1024 * 1024;

// ── TV graphics v2 (design jvm notes/ott/tv-graphics/design_tv_graphics_20260928.md) ──

/**
 * The factory's quality warning codes (C1) in the customer's words, each
 * with the fix. An unknown code (a newer factory) renders neutrally.
 */
export const TV_QUALITY_WARNING_COPY: Record<string, string> = {
  wide_logo_in_circle:
    'Your logo is wide. On Google TV it appears in a circle and will look tiny — add a square symbol.',
  low_contrast: 'Your logo and background colour are too close — pick a stronger ground colour.',
  small_fill: 'Your logo fills little of the tile — use a tighter crop or a symbol.',
  text_fallback:
    'We set your app name in type because there is no wordmark — upload one to use your own lettering.',
};

/** The Brand kit's TV wording (the Android-family set). */
export const TV_BRAND_KIT_COPY = {
  symbolLabel: 'Symbol',
  symbolWhy: 'Google TV shows your app as a circle — a square mark fills it.',
  symbolHelp: 'A square transparent PNG of your logo mark, at least 1024 x 1024.',
  wordmarkLabel: 'Wordmark (optional)',
  wordmarkHelp: 'Your wide logo or lettering, transparent PNG — it goes beside or under the symbol on the wide tiles.',
  appNameLabel: 'App name on the tiles',
  appNameHelp: 'Set in type on the wide tiles when there is no wordmark.',
  groundLabel: 'Ground colour',
  groundHelp: 'Fills every tile. A strong brand colour stands out on a TV home screen.',
  suggestedGround: 'Use the colour of your symbol',
  tileArtLabel: 'Tile art (optional)',
  tileArtHelp: 'A 16:9 picture behind the Fire TV home tile.',
  wideSymbolWarning: 'This mark is wide. A square symbol fills the Google TV circle much better.',
  noTransparency: 'This image has no transparent pixels. A transparent PNG sits cleanly on the ground colour.',
  tooSmallSymbol: 'This symbol is smaller than 1024 x 1024 and may look soft on a 4K TV.',
  v1Notice:
    'Your TV tiles can look better. Regenerate with a square symbol — nothing changes until you approve the new set.',
} as const;

/** Upper bound of the app name set in type on the tiles. */
export const TV_TILE_TEXT_MAX = 40;
/** A symbol whose longer side exceeds this multiple of the shorter one reads small in a circle. */
export const TV_SYMBOL_MAX_ASPECT = 1.6;
/** The smallest symbol side that stays crisp on a 4K TV. */
export const TV_SYMBOL_MIN_PX = 1024;

/**
 * Preview geometry on a 1920 x 1080 frame. The Google TV circle: the
 * launcher icon is 80 dp (160 px at 1080p, xhdpi) and shows the central
 * 72 dp of the 108 dp adaptive layers. The Apps-page banner: 160 x 90 dp.
 * The Fire TV home tile: approximate (Amazon publishes the 1280 x 720 art
 * and its 882 x 448 content-safe area, not the tile's on-screen size).
 */
export const TV_PREVIEW_GEOMETRY = {
  frameW: 1920,
  frameH: 1080,
  googleTvCirclePx: 160,
  adaptiveVisibleFrac: 72 / 108,
  googleTvBannerPx: { w: 320, h: 180 },
  fireTvTilePx: { w: 336, h: 189 },
  fireTvSafeArea: { w: 882, h: 448, of: { w: 1280, h: 720 } },
} as const;

